import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  BookText,
  Lightbulb,
  Sparkles,
  ScrollText,
  X,
  CheckCircle2,
  AlertCircle,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { ContentStatus } from '@jeraisonne/db';
import type { Lesson, Competence } from '@jeraisonne/db';
import { useAdminChapterDetail, type ImportLog, type ImportLogEntry } from '@/lib/admin/queries';
import {
  useCreateLesson,
  useDeleteLesson,
  useCreateCompetence,
  useDeleteCompetence,
  slugify,
} from '@/lib/admin/mutations';
import { useImportLesson, type ImportLessonResult } from '@/lib/admin/import-lesson-mutation';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

const STATUS_STYLES: Record<ContentStatus, string> = {
  published: 'bg-xp-100 text-xp-700',
  draft: 'bg-ink-100 text-ink-600',
  archived: 'bg-accent-50 text-accent-600',
  awaiting_review: 'bg-amber-50 text-amber-700',
};

const STATUS_LABEL: Record<ContentStatus, string> = {
  published: 'Publié',
  draft: 'Brouillon',
  archived: 'Archivé',
  awaiting_review: 'En relecture',
};

type Tab = 'lessons' | 'competences';

// ── Inline create lesson form ──────────────────────────────────────────────────

function NewLessonForm({ chapterId, onDone }: { chapterId: string; onDone: () => void }) {
  const navigate = useNavigate();
  const createLesson = useCreateLesson();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [order, setOrder] = useState(1);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(slugify(val));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !slug.trim()) return;
    const result = await createLesson.mutateAsync({
      chapter_id: chapterId,
      title: title.trim(),
      slug: slug.trim(),
      display_order: order,
    });
    navigate({ to: '/tutor/admin/lessons/$lessonId', params: { lessonId: result.id } });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-primary-50 mt-2 space-y-2 rounded-xl p-3">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Titre de la leçon"
          className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 col-span-2 rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2 sm:col-span-1"
          required
          autoFocus
        />
        <input
          type="text"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="slug"
          className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 col-span-2 rounded-lg border px-2.5 py-2 font-mono text-[13px] focus:outline-none focus:ring-2 sm:col-span-1"
          required
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          value={order}
          onChange={(e) => setOrder(parseInt(e.target.value, 10) || 1)}
          className="border-ink-200 text-ink-900 focus:ring-primary-400 w-16 rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
        />
        <span className="text-ink-500 font-sans text-[12px]">Ordre</span>
        <div className="flex-1" />
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" size="sm" disabled={createLesson.isPending || !title.trim()}>
          {createLesson.isPending ? '...' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}

// ── Inline create competence form ──────────────────────────────────────────────

function NewCompetenceForm({ chapterId, onDone }: { chapterId: string; onDone: () => void }) {
  const navigate = useNavigate();
  const createCompetence = useCreateCompetence();
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [order, setOrder] = useState(1);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(slugify(val));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !slug.trim()) return;
    const result = await createCompetence.mutateAsync({
      chapter_id: chapterId,
      title: title.trim(),
      slug: slug.trim(),
      display_order: order,
    });
    navigate({ to: '/tutor/admin/competences/$competenceId', params: { competenceId: result.id } });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-accent-50 mt-2 space-y-2 rounded-xl p-3">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Titre de la compétence"
          className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-accent-400 col-span-2 rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2 sm:col-span-1"
          required
          autoFocus
        />
        <input
          type="text"
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          placeholder="slug"
          className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-accent-400 col-span-2 rounded-lg border px-2.5 py-2 font-mono text-[13px] focus:outline-none focus:ring-2 sm:col-span-1"
          required
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          value={order}
          onChange={(e) => setOrder(parseInt(e.target.value, 10) || 1)}
          className="border-ink-200 text-ink-900 focus:ring-accent-400 w-16 rounded-lg border px-2.5 py-2 font-sans text-[13px] focus:outline-none focus:ring-2"
        />
        <span className="text-ink-500 font-sans text-[12px]">Ordre</span>
        <div className="flex-1" />
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" size="sm" disabled={createCompetence.isPending || !title.trim()}>
          {createCompetence.isPending ? '...' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}

// ── Import log modal ───────────────────────────────────────────────────────────

const LEVEL_ICON: Record<ImportLogEntry['level'], React.ReactNode> = {
  info: <Info className="text-primary-500 mt-0.5 h-3.5 w-3.5 shrink-0" />,
  warn: <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />,
  error: <AlertCircle className="text-accent-500 mt-0.5 h-3.5 w-3.5 shrink-0" />,
};

function ImportLogModal({ log, onClose }: { log: ImportLog; onClose: () => void }) {
  const duration = log.finished_at
    ? Math.round((new Date(log.finished_at).getTime() - new Date(log.started_at).getTime()) / 1000)
    : null;

  return (
    <div
      className="bg-ink-900/50 fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[85dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:max-w-xl sm:rounded-2xl">
        {/* Header */}
        <div className="border-ink-100 flex shrink-0 items-center gap-2 border-b px-4 py-3">
          <ScrollText className="text-ink-500 h-4 w-4 shrink-0" />
          <span className="font-display text-ink-900 flex-1 text-[14px] font-semibold">
            Journal d'import IA
          </span>
          <div className="flex items-center gap-2">
            {log.status === 'success' ? (
              <span className="font-display text-xp-700 bg-xp-100 flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                <CheckCircle2 className="h-3 w-3" /> Succès
              </span>
            ) : log.status === 'error' ? (
              <span className="font-display text-accent-700 bg-accent-100 flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                <AlertCircle className="h-3 w-3" /> Erreur
              </span>
            ) : (
              <span className="font-display text-ink-500 bg-ink-100 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                En cours
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-700 hover:bg-ink-100 rounded-lg p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Meta */}
        <div className="bg-ink-50 border-ink-100 shrink-0 border-b px-4 py-2">
          <p className="text-ink-500 font-mono text-[11px]">
            Démarré : {new Date(log.started_at).toLocaleString('fr-FR')}
            {duration !== null && ` · Durée : ${duration}s`}
          </p>
        </div>

        {/* Entries */}
        <div className="flex-1 space-y-1.5 overflow-y-auto px-4 py-3">
          {log.entries.map((entry, i) => (
            <div key={i} className="flex items-start gap-2">
              {LEVEL_ICON[entry.level]}
              <div className="min-w-0 flex-1">
                <p className="text-ink-800 font-sans text-[12px] leading-snug">{entry.message}</p>
                <p className="text-ink-400 font-mono text-[10px]">
                  {new Date(entry.ts).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-ink-100 shrink-0 border-t px-4 py-3">
          <Button size="sm" variant="outline" className="w-full" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function TutorAdminChapterDetailPage() {
  const { chapterId } = useParams({ strict: false }) as { chapterId: string };
  const { data: chapter, isLoading } = useAdminChapterDetail(chapterId);
  const deleteLesson = useDeleteLesson();
  const deleteCompetence = useDeleteCompetence();
  const importLesson = useImportLesson();
  const [tab, setTab] = useState<Tab>('lessons');
  const [showNewLesson, setShowNewLesson] = useState(false);
  const [showNewCompetence, setShowNewCompetence] = useState(false);
  const [importResult, setImportResult] = useState<ImportLessonResult | null>(null);
  const [showLog, setShowLog] = useState(false);

  function confirmDeleteLesson(lesson: Lesson) {
    if (!confirm(`Supprimer la leçon "${lesson.title}" ?`)) return;
    deleteLesson.mutate({ id: lesson.id, chapter_id: chapterId });
  }

  function confirmDeleteCompetence(comp: Competence) {
    if (!confirm(`Supprimer la compétence "${comp.title}" ?`)) return;
    deleteCompetence.mutate({ id: comp.id, chapter_id: chapterId });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="p-6 text-center">
        <p className="font-display text-ink-600 font-semibold">Chapitre introuvable.</p>
        <Link
          to="/tutor/admin/chapters"
          className="text-primary-600 mt-2 inline-block text-[13px] underline"
        >
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        {/* Back */}
        <Link
          to="/tutor/admin/chapters"
          className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Chapitres
        </Link>

        {/* Header */}
        <div className="mb-6 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-ink-900 text-[22px] font-extrabold leading-tight">
              {chapter.title}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <span className="bg-primary-100 text-primary-700 font-display rounded-full px-2 py-0.5 text-[11px] font-semibold">
                {SUBJECT_LABEL[chapter.subject] ?? chapter.subject}
              </span>
              <span className="bg-level-100 text-level-700 font-display rounded-full px-2 py-0.5 text-[11px] font-semibold">
                {LEVEL_LABEL[chapter.level] ?? chapter.level}
              </span>
              <span
                className={cn(
                  'font-display rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  STATUS_STYLES[chapter.status],
                )}
              >
                {STATUS_LABEL[chapter.status]}
              </span>
            </div>
          </div>
          <Link to="/tutor/admin/chapters/$chapterId/edit" params={{ chapterId: chapter.id }}>
            <Button size="sm" variant="outline" className="flex shrink-0 items-center gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              Modifier
            </Button>
          </Link>
        </div>

        {/* Tabs */}
        <div className="bg-ink-100 mb-5 flex gap-1 rounded-xl p-1">
          {(['lessons', 'competences'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'font-display flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-[13px] font-semibold transition-colors',
                tab === t ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500 hover:text-ink-700',
              )}
            >
              {t === 'lessons' ? (
                <BookText className="h-3.5 w-3.5" />
              ) : (
                <Lightbulb className="h-3.5 w-3.5" />
              )}
              {t === 'lessons'
                ? `Leçons (${chapter.lessons.length})`
                : `Compétences (${chapter.competences.length})`}
            </button>
          ))}
        </div>

        {/* Lessons tab */}
        {tab === 'lessons' && (
          <div>
            {chapter.lessons.length > 0 ? (
              <div className="border-ink-200 divide-ink-100 mb-3 divide-y overflow-hidden rounded-2xl border bg-white">
                {chapter.lessons.map((lesson) => (
                  <div key={lesson.id} className="flex items-center gap-2 p-3">
                    <span className="text-ink-400 w-6 shrink-0 text-center font-mono text-[11px]">
                      {lesson.display_order}
                    </span>
                    <Link
                      to="/tutor/admin/lessons/$lessonId"
                      params={{ lessonId: lesson.id }}
                      className="group min-w-0 flex-1 active:scale-[0.99]"
                    >
                      <p className="font-display text-ink-900 group-hover:text-primary-700 truncate text-[14px] font-semibold transition-colors">
                        {lesson.title}
                      </p>
                      <p className="text-ink-400 truncate font-mono text-[11px]">{lesson.slug}</p>
                    </Link>
                    <span
                      className={cn(
                        'font-display shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                        STATUS_STYLES[lesson.status],
                      )}
                    >
                      {STATUS_LABEL[lesson.status]}
                    </span>
                    <Link to="/tutor/admin/lessons/$lessonId/edit" params={{ lessonId: lesson.id }}>
                      <Button size="sm" variant="outline" className="shrink-0">
                        Modifier
                      </Button>
                    </Link>
                    <button
                      onClick={() => confirmDeleteLesson(lesson)}
                      disabled={deleteLesson.isPending}
                      className="text-ink-400 hover:text-accent-600 hover:bg-accent-50 rounded-lg p-1.5 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-ink-400 mb-3 font-sans text-[13px]">
                Aucune leçon pour ce chapitre.
              </p>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                size="sm"
                variant="outline"
                className="flex min-h-[2.75rem] w-full items-center gap-1.5 active:scale-[0.98] sm:w-auto"
                onClick={() =>
                  importLesson.mutate(chapterId, {
                    onSuccess: (result) => {
                      setImportResult(result);
                      setShowNewLesson(false);
                    },
                  })
                }
                disabled={importLesson.isPending}
              >
                {importLesson.isPending ? (
                  <>
                    <span className="border-primary-500 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-t-transparent" />
                    Importation… (30–60 s)
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 shrink-0" />
                    Importer avec l'IA
                  </>
                )}
              </Button>

              {!showNewLesson && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex min-h-[2.75rem] w-full items-center gap-1.5 sm:w-auto"
                  onClick={() => setShowNewLesson(true)}
                >
                  <Plus className="h-4 w-4" />
                  Nouvelle leçon
                </Button>
              )}
            </div>

            {importResult && (
              <p className="text-xp-600 mt-2 font-sans text-[12px]">
                ✓ Leçon et {importResult.exerciseCount} exercices importés en brouillon
              </p>
            )}
            {importLesson.isError && (
              <p className="text-accent-600 mt-2 font-sans text-[12px]">
                Erreur : {(importLesson.error as Error)?.message}
              </p>
            )}
            {chapter.last_import_log && (
              <button
                onClick={() => setShowLog(true)}
                className="text-ink-500 hover:text-ink-800 mt-1 flex items-center gap-1.5 font-sans text-[12px] transition-colors"
              >
                <ScrollText className="h-3.5 w-3.5" />
                Voir le journal du dernier import
              </button>
            )}

            {showNewLesson && (
              <NewLessonForm chapterId={chapterId} onDone={() => setShowNewLesson(false)} />
            )}
          </div>
        )}

        {/* Competences tab */}
        {tab === 'competences' && (
          <div>
            {chapter.competences.length > 0 ? (
              <div className="border-ink-200 divide-ink-100 mb-3 divide-y overflow-hidden rounded-2xl border bg-white">
                {chapter.competences.map((comp) => (
                  <div key={comp.id} className="flex items-center gap-2 p-3">
                    <span className="text-ink-400 w-6 shrink-0 text-center font-mono text-[11px]">
                      {comp.display_order}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
                        {comp.title}
                      </p>
                      <p className="text-ink-400 truncate font-mono text-[11px]">{comp.slug}</p>
                    </div>
                    {comp.tags.length > 0 && (
                      <div className="hidden shrink-0 gap-1 sm:flex">
                        {comp.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="bg-ink-100 text-ink-500 rounded px-1.5 py-0.5 font-sans text-[10px]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <Link
                      to="/tutor/admin/competences/$competenceId"
                      params={{ competenceId: comp.id }}
                    >
                      <Button size="sm" variant="outline" className="shrink-0">
                        Modifier
                      </Button>
                    </Link>
                    <button
                      onClick={() => confirmDeleteCompetence(comp)}
                      disabled={deleteCompetence.isPending}
                      className="text-ink-400 hover:text-accent-600 hover:bg-accent-50 rounded-lg p-1.5 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-ink-400 mb-3 font-sans text-[13px]">
                Aucune compétence pour ce chapitre.
              </p>
            )}

            {showNewCompetence ? (
              <NewCompetenceForm chapterId={chapterId} onDone={() => setShowNewCompetence(false)} />
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1.5"
                onClick={() => setShowNewCompetence(true)}
              >
                <Plus className="h-4 w-4" />
                Nouvelle compétence
              </Button>
            )}
          </div>
        )}
      </div>

      {showLog && chapter.last_import_log && (
        <ImportLogModal log={chapter.last_import_log} onClose={() => setShowLog(false)} />
      )}
    </>
  );
}
